import { CommodityDetail } from 'features/contracts/enums/commodityDetail';
import MeasuredDataResultDTO from 'features/shared/dtos/MeasuredDataResultDTO';
import React, { useState } from 'react';
import PageCard from '../card/PageCard';
import TabsNavigation from '../navs/TabsNavigation';
import Tab from '../navs/Tab';
import styles from './measuredDataGraphCard.module.scss';
import colors from 'styles/colors.module.scss';
import { useTranslation } from 'react-i18next';
import { ColorClassSuffix } from 'features/shared/enums/colorClassSuffix';
import { GraphType } from './graphType';
import MeasuredValueDTO from 'features/shared/dtos/MeasuredValueDTO';
import MeasuredDataGraph from './MeasuredDataGraph';
import { getGraphLabel, getGraphDataTypeTranslation } from './measuredDataGraphHelper';
import { getMeasuredDataType } from '../measuredData/measuredDataHelper';
import { GraphViewMode } from './models/GraphViewMode';

type Props = {
  commodityDetail: CommodityDetail;
  realValues: MeasuredDataResultDTO;
  planValues?: MeasuredDataResultDTO;
  showLegend?: boolean;
  adaptiveContainerHeight?: boolean;
  isProduction?: boolean;
  viewMode?: GraphViewMode;
  onLineGraphClick?: (value: MeasuredValueDTO) => void;
  renderAdditionalInfo?: () => JSX.Element;
}

const MeasuredDataGraphCard = ({ 
  commodityDetail,
  realValues,
  planValues,
  showLegend,
  adaptiveContainerHeight,
  isProduction,
  viewMode,
  onLineGraphClick,
  renderAdditionalInfo,
 }: Props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;
 
  // state
  const [graphType, setGraphType] = useState<GraphType>("bar");

  // helpers
  const getColor = (): string => {
    switch (commodityDetail) {
      case "ElectricityCustomer":
      case "ElectricitySupplier":
        return isProduction
          ? colors[ColorClassSuffix.Production]
          : colors[ColorClassSuffix.Electricity];
      case "GasCustomer":
      case "GasSupplier":
        return colors[ColorClassSuffix.Gas];
      case "HeatCustomer":
        return colors[ColorClassSuffix.Heat];
      case "eMobility":
        return colors[ColorClassSuffix.Emobility];
      default:
        return colors[ColorClassSuffix.Default];
    }
  };

  const getUnit = (values: MeasuredValueDTO[]) => {
    let item = values?.length > 0 ? values[0] : null;
    return item !== null ? `[${item.unit}]` : "";
  };

  const getCardTitle = () => {
    if (viewMode) {
      return tLabels(
        `measuredDataGraphCard.${getMeasuredDataType(
          commodityDetail,
          isProduction
        )}.${viewMode === GraphViewMode.DayDetail ? "dayDetail" : "monthOverview"}`
      );
    }

    let translation = getGraphDataTypeTranslation(
      tLabels,
      commodityDetail,
      "title",
      true,
      isProduction
    );

    return `${tLabels("measuredDataGraphCard.graph")} ${translation}`;
  }

  const getMeasuredDataGraphLabel = (dataType: "real" | "plan") => { 
    return getGraphLabel(
      tLabels,
      commodityDetail,
      dataType,
      isProduction,
      planValues !== undefined
    )
  }

  // sub-renders
  const renderSwitchButtons = () => {

    return (
      <TabsNavigation className={styles.headerBtnsItem}>
        <Tab 
          cardHeaderTab 
          onClick={() => setGraphType("bar")} 
          active={graphType === "bar"}
          className="px-3"
        >
          {tLabels("measuredDataGraphCard.bar")}
        </Tab>
        <Tab 
          cardHeaderTab 
          onClick={() => setGraphType("line")} 
          active={graphType === "line"}
          className="px-3"
        >
          {tLabels("measuredDataGraphCard.line")}
        </Tab>
      </TabsNavigation>
    )
  }

  // render
  return (
    <PageCard 
      title={getCardTitle()}
      subtitle={getUnit(realValues.measuredValues!)}
      headerActionBtns={() => {
        const switchButtons = renderSwitchButtons();
        return switchButtons ? [switchButtons] : [];
      }}
    >
      <MeasuredDataGraph
        graphType={graphType}
        quotationType={realValues.quotationType!}
        realValues={realValues.measuredValues!}
        realValuesLabel={getMeasuredDataGraphLabel("real")}
        realValuesColor={getColor()}
        planValues={planValues ? planValues.measuredValues! : undefined}
        planValuesLabel={getMeasuredDataGraphLabel("plan")}
        showLegend={showLegend}
        containerClass={adaptiveContainerHeight ? styles.adaptiveContainerHeight : styles.chartContainter}
        onLineGraphClick={viewMode === GraphViewMode.DayDetail ? undefined : onLineGraphClick}
      />
      {renderAdditionalInfo && renderAdditionalInfo()}
    </PageCard>
  );
}

export default MeasuredDataGraphCard;